import React, { useEffect, useRef, useState, useLayoutEffect } from 'react';
import { ATELIER } from '../tokens';

/**
 * MemoryRibbon - an absolutely-positioned SVG curve that visually connects a
 * referencing turn (e.g. user said "create an image of it") back to the prior
 * turn it referenced.
 *
 * Implementation: the parent <Thread> assigns a `data-turn-id` to every turn
 * row. This component finds the source + target DOM nodes, computes the
 * coordinate of each turn's left-rail badge, and draws a bezier curve between
 * them. On hover, the ribbon strengthens (full violet, glow) and reveals a
 * "from your earlier prompt" tooltip near the source.
 *
 * Container ref must be a positioned element that is the offsetParent of both
 * turn rows.
 */
export function MemoryRibbon({ containerRef, sourceTurnId, targetTurnId }) {
  const [geom, setGeom] = useState(null);
  const [hover, setHover] = useState(false);
  const rafRef = useRef(0);

  const measure = () => {
    const root = containerRef?.current;
    if (!root) return;
    const src = root.querySelector(`[data-turn-id="${CSS.escape(sourceTurnId)}"] [data-badge]`);
    const tgt = root.querySelector(`[data-turn-id="${CSS.escape(targetTurnId)}"] [data-badge]`);
    if (!src || !tgt) { setGeom(null); return; }

    const rootRect = root.getBoundingClientRect();
    const sRect = src.getBoundingClientRect();
    const tRect = tgt.getBoundingClientRect();

    // Anchor on the right edge of each badge (flow into the body)
    const sx = sRect.left  - rootRect.left + sRect.width  / 2;
    const sy = sRect.top   - rootRect.top  + sRect.height / 2;
    const tx = tRect.left  - rootRect.left + tRect.width  / 2;
    const ty = tRect.top   - rootRect.top  + tRect.height / 2;

    setGeom({ sx, sy, tx, ty, w: rootRect.width, h: rootRect.height });
  };

  useLayoutEffect(() => {
    measure();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceTurnId, targetTurnId]);

  useEffect(() => {
    const onResize = () => {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(measure);
    };
    window.addEventListener('resize', onResize);
    // Re-measure on mutation (turns may add content as they finish)
    const root = containerRef?.current;
    let mo;
    if (root && typeof MutationObserver !== 'undefined') {
      mo = new MutationObserver(onResize);
      mo.observe(root, { childList: true, subtree: true, attributes: true, attributeFilter: ['style'] });
    }
    return () => {
      window.removeEventListener('resize', onResize);
      cancelAnimationFrame(rafRef.current);
      if (mo) mo.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!geom) return null;

  // Build a soft S-curve from source down to target.
  // The curve dips to the LEFT of the badges, so it doesn't overlap content.
  const dy = geom.ty - geom.sy;
  const ctrlOffset = Math.max(40, Math.min(160, Math.abs(dy) * 0.4));
  const leftOffset = 22;
  const path = [
    `M ${geom.sx} ${geom.sy}`,
    `C ${geom.sx - leftOffset} ${geom.sy + ctrlOffset / 1.4},`,
    `  ${geom.tx - leftOffset} ${geom.ty - ctrlOffset / 1.4},`,
    `  ${geom.tx} ${geom.ty}`,
  ].join(' ');

  return (
    <svg
      aria-hidden
      style={{
        position: 'absolute',
        inset: 0,
        width: '100%', height: '100%',
        pointerEvents: 'none',
        zIndex: 1,
      }}
      preserveAspectRatio="none"
    >
      <defs>
        <linearGradient id={`ribbonGrad-${sourceTurnId}`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%"   stopColor="#A78BFA" stopOpacity="0.85" />
          <stop offset="50%"  stopColor="#C4B5FD" stopOpacity="0.95" />
          <stop offset="100%" stopColor="#7C3AED" stopOpacity="0.85" />
        </linearGradient>
      </defs>

      {/* Hover-target hit area (wide, transparent stroke) */}
      <path
        d={path}
        stroke="transparent"
        strokeWidth="20"
        fill="none"
        style={{ pointerEvents: 'stroke' }}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
      />

      {/* Visible ribbon */}
      <path
        d={path}
        stroke={`url(#ribbonGrad-${sourceTurnId})`}
        strokeWidth={hover ? 2 : 1.25}
        strokeLinecap="round"
        strokeDasharray={hover ? 'none' : '5 4'}
        fill="none"
        className={hover ? 'dgw-ribbon-glow' : ''}
        style={{
          opacity: hover ? 1 : 0.55,
          transition: 'opacity 0.18s, stroke-width 0.18s',
        }}
      />

      {/* Endpoint dots */}
      <circle cx={geom.sx} cy={geom.sy} r="2.5" fill={ATELIER.accent} opacity={hover ? 1 : 0.55} />
      <circle cx={geom.tx} cy={geom.ty} r="2.5" fill={ATELIER.accent} opacity={hover ? 1 : 0.55} />
    </svg>
  );
}
